import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {useQueryClient} from '@github-ui/react-query'
import {updateIssueSubscriptionMutation as updateIssueSubscriptionMutationQueryId} from '@github-ui/server-persisted-queries'
import type {
  ThreadSubscriptionEvent,
  UpdateIssueSubscriptionMutationMutation,
} from '@github-ui/server-persisted-queries/types/updateIssueSubscriptionMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

import {issueViewerSecondaryViewQueryKey} from '../components/fetch-issue-viewer-secondary-view'
import {
  type SubscriptionStatus,
  subscriptionStatusQueryKey,
} from '../components/sections/subscription-section/fetch-subscription-status'
import {LABELS} from '../constants/labels'
import {MESSAGES} from '../constants/messages'

export type SubscriptionUpdate = {
  state: 'SUBSCRIBED' | 'UNSUBSCRIBED' | 'CUSTOM'
  events?: readonly ThreadSubscriptionEvent[]
}

type SubscriptionCallbacks = {
  onCompleted?: () => void
  onError: (error: Error) => void
}

const ERROR_MESSAGE = 'Failed to update issue subscription'

function toOperation(issue: SubscriptionStatus): SetFieldsOperation {
  return {
    type: 'setFields',
    id: issue.id,
    fields: {
      viewerThreadSubscriptionFormAction: issue.viewerThreadSubscriptionFormAction,
      viewerCustomSubscriptionEvents: issue.viewerCustomSubscriptionEvents,
    },
  }
}

function inputFromOperation(operation: SetFieldsOperation): SubscriptionUpdate {
  const action = operation.fields.viewerThreadSubscriptionFormAction
  const events = operation.fields.viewerCustomSubscriptionEvents
  if (action !== 'SUBSCRIBE' && action !== 'UNSUBSCRIBE') throw new Error(ERROR_MESSAGE)
  if (events !== null && (!Array.isArray(events) || !events.every(isSubscriptionEvent))) {
    throw new Error(ERROR_MESSAGE)
  }
  return {
    state: action === 'SUBSCRIBE' ? 'UNSUBSCRIBED' : events?.length ? 'CUSTOM' : 'SUBSCRIBED',
    ...(events ? {events} : {}),
  }
}

function isSubscriptionEvent(event: unknown): event is ThreadSubscriptionEvent {
  return event === LABELS.notifications.closedLabel || event === LABELS.notifications.reopenedLabel
}

async function updateSubscriptionRequest(
  path: string,
  issueId: string,
  input: SubscriptionUpdate,
): Promise<SubscriptionStatus> {
  const query = new URLSearchParams({queryId: updateIssueSubscriptionMutationQueryId})
  const response = await reactFetchJSON(`${path}?${query}`, {method: 'PUT', body: input})
  if (!response.ok) throw new Error(`${ERROR_MESSAGE} (${response.status})`)

  const result = (await response.json()) as {
    data?: UpdateIssueSubscriptionMutationMutation | null
    errors?: ReadonlyArray<{message?: string}> | null
  } | null
  if (result?.errors?.length) throw new Error(result.errors[0]?.message ?? ERROR_MESSAGE)
  const issue = result?.data?.updateSubscription?.subscribable
  if (
    issue?.__typename !== 'Issue' ||
    issue.id !== issueId ||
    issue.viewerThreadSubscriptionFormAction === undefined ||
    issue.viewerCustomSubscriptionEvents === undefined
  ) {
    throw new Error(ERROR_MESSAGE)
  }
  return issue
}

export function useUpdateIssueSubscription({
  issueId,
  nameWithOwner,
  issueNumber,
}: {
  issueId: string
  nameWithOwner: string
  issueNumber: number
}) {
  const queryClient = useQueryClient()
  const [owner = '', repo = ''] = nameWithOwner.split('/')
  const {mutateAsync} = useEntityMutation<SubscriptionStatus, Error>({
    scope: {id: `issue-subscription:${issueId}`},
    mutationFn: operation =>
      updateSubscriptionRequest(
        `/${nameWithOwner}/issues/${issueNumber}/subscription`,
        issueId,
        inputFromOperation(operation),
      ),
    getConfirmedOperation: toOperation,
    onSuccess: async issue => {
      // Also settle known caches whose controls unmounted while this request was pending.
      await settleConfirmedOperation({
        queryClient,
        ownerId: issueId,
        operation: toOperation(issue),
        targets: [
          {queryKey: subscriptionStatusQueryKey(owner, repo, issueNumber)},
          {queryKey: issueViewerSecondaryViewQueryKey(owner, repo, issueNumber)},
        ],
      })
    },
  })

  const updateSubscription = useCallback(
    (input: SubscriptionUpdate, callbacks: SubscriptionCallbacks) => {
      if (input.state === 'CUSTOM' && !input.events?.length) {
        callbacks.onError(new Error(MESSAGES.customSelectionMissing))
        return
      }
      const operation = toOperation({
        id: issueId,
        viewerThreadSubscriptionFormAction: input.state === 'UNSUBSCRIBED' ? 'SUBSCRIBE' : 'UNSUBSCRIBE',
        viewerCustomSubscriptionEvents: input.state === 'CUSTOM' ? [...(input.events ?? [])] : null,
      })
      // Per-call mutate callbacks only run for the latest observer. Each queued write needs its own outcome.
      const complete = async () => {
        try {
          await mutateAsync({operation})
        } catch (error) {
          callbacks.onError(error instanceof Error ? error : new Error(ERROR_MESSAGE, {cause: error}))
          return
        }
        callbacks.onCompleted?.()
      }
      void complete()
    },
    [issueId, mutateAsync],
  )

  return {updateSubscription}
}
